/**
 * The most roles one multi-role company card holds (HitCompanyGroupCard /
 * HitCompanyGroupCardView). A company with more roles on the page gets several consecutive
 * cards of up to this many roles each — cards stay a uniform, scannable size instead of one
 * employer's card growing into a tower that swallows the results grid.
 */
export const MAX_ROLES_PER_GROUP_CARD = 4;

/** A same-company card's worth of hits (2..MAX_ROLES_PER_GROUP_CARD of them). */
export interface CompanyHitGroup<T> {
  type: 'group';
  companyId: string;
  items: T[];
  /** Which of this company's cards this is (0-based) — for stable React keys when one company yields several cards. */
  partIndex: number;
}

export interface SingleHitEntry<T> {
  type: 'single';
  item: T;
}

export type GroupedHitEntry<T> = SingleHitEntry<T> | CompanyHitGroup<T>;

/**
 * Similarity key for deciding which of a company's roles share a card when it has more than
 * MAX_ROLES_PER_GROUP_CARD on the page: primary the normalized job field/category, secondary the
 * title, so each card tends to hold one theme ("the engineering roles", "the sales roles") and
 * near-identical titles sit together. Both the interactive SRP and the static/SEO shell must
 * build this from the same underlying values (hit.field, normalised title) or hydration would
 * reshuffle the cards.
 */
export function buildRoleClusterKey(field: string | null | undefined, title: string | null | undefined): string {
  return `${(field ?? '').trim().toLowerCase()} ${(title ?? '').trim().toLowerCase()}`;
}

/**
 * Groups a page of SRP hits by company id, so the card grid can render multi-role cards for an
 * employer with several open roles on this page instead of several near-identical cards.
 * Same-page only, by design: this only sees whatever hits the caller already fetched for the
 * current page/query, so it can't (and shouldn't) reach across pagination to merge a company's
 * roles that landed on a different page.
 *
 * A company with more than MAX_ROLES_PER_GROUP_CARD roles is split into several consecutive
 * group cards of at most that many roles each, ordered/partitioned by `getRoleClusterKey` when
 * given (similar roles share a card; ties keep result order) and rebalanced so no card is left
 * holding a single orphan role.
 *
 * Preserves first-occurrence order — a later hit from a company already seen on this page joins
 * that earlier group in place (or that company's run of cards, when split), rather than moving
 * to the later hit's position. Hits with no resolvable company id (rare — a bad
 * companyId/companyName pair) are never grouped, including with each other, since an empty id
 * isn't a real shared identity.
 */
export function groupHitsByCompany<T>(
  hits: readonly T[],
  getCompanyId: (hit: T) => string | null | undefined,
  getRoleClusterKey?: (hit: T) => string,
): GroupedHitEntry<T>[] {
  const collected: GroupedHitEntry<T>[] = [];
  const groupIndexByCompanyId = new Map<string, number>();

  for (const hit of hits) {
    const companyId = getCompanyId(hit)?.trim();
    if (!companyId) {
      collected.push({ type: 'single', item: hit });
      continue;
    }

    const existingIndex = groupIndexByCompanyId.get(companyId);
    if (existingIndex === undefined) {
      groupIndexByCompanyId.set(companyId, collected.length);
      collected.push({ type: 'single', item: hit });
      continue;
    }

    const existing = collected[existingIndex];
    if (existing.type === 'single') {
      collected[existingIndex] = { type: 'group', companyId, items: [existing.item, hit], partIndex: 0 };
    } else {
      existing.items.push(hit);
    }
  }

  // Second pass: split any oversized group into consecutive cards of at most
  // MAX_ROLES_PER_GROUP_CARD roles, clustered by similarity.
  const result: GroupedHitEntry<T>[] = [];
  for (const entry of collected) {
    if (entry.type === 'single' || entry.items.length <= MAX_ROLES_PER_GROUP_CARD) {
      result.push(entry);
      continue;
    }

    // Stable sort by cluster key (original result order breaks ties) so chunk boundaries fall
    // between clusters as much as a fixed card size allows.
    const ordered = getRoleClusterKey
      ? entry.items
          .map((item, originalIndex) => ({ item, originalIndex, key: getRoleClusterKey(item) }))
          .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : a.originalIndex - b.originalIndex))
          .map((x) => x.item)
      : entry.items;

    const chunks: T[][] = [];
    for (let i = 0; i < ordered.length; i += MAX_ROLES_PER_GROUP_CARD) {
      chunks.push(ordered.slice(i, i + MAX_ROLES_PER_GROUP_CARD));
    }
    // A trailing 1-role card would defeat the point of grouping — rebalance it to 2 by taking a
    // role from the (necessarily full) card before it.
    const last = chunks[chunks.length - 1];
    if (last.length === 1) {
      const donorRole = chunks[chunks.length - 2].pop();
      if (donorRole !== undefined) last.unshift(donorRole);
    }

    chunks.forEach((items, partIndex) => {
      result.push({ type: 'group', companyId: entry.companyId, items, partIndex });
    });
  }

  return result;
}
